<template>
  <v-form v-model="valid" class="py-3">
    <Notification
      :content="notificationContent"
      :type="notificationStatus"
      :isNotificationOpen="isNotificationOpen"
      @notificationClosed="closeNotification"
    />
    <a id="rule-details-back-btn-link" @click="backToRules">
      <v-icon color="white">mdi-chevron-left</v-icon>
      <span class="rule-details-back-btn-label">{{ $t('map.tolldomains.actions.back_to_rules') }}</span>
    </a>
    <div class="rule-details-title mx-2 mt-3">
      <div id="rule-details-header-rule-name">{{ getTollDomainInfo.masterName }} - {{ getTollDomainInfo.name }}</div>
      <div>
        <v-btn
          v-if="!isReadOnly"
          id="rule-details-save-btn"
          class="ctp-btn my-0"
          dark
          rounded
          :disabled="disableSave()"
          @click="handleSave"
        >
          {{ saveLabel }}
        </v-btn>
        <v-btn
          id="rule-details-version-history-btn"
          class="ctp-btn my-0 ml-4 version-history-btn"
          dark
          rounded
          outlined
          @click="toggleVersionHistory"
        >
          {{ $t(versionHistoryBtnLabel) }}
        </v-btn>
      </div>
      <ConfirmDialog
        :headline="modalData.headline"
        :text="modalData.text"
        :alternative="true"
        :actions="modalData.actions"
        :initialDisplayState="saving"
        @modal="saving = false"
      />
    </div>
    <v-row no-gutters>
      <v-col cols="4">
        <div class="rule-details-card ml-2 mr-4">
          <div class="rule-details-card-title">
            <span id="rule-details-global-information-title">{{ $t('map.tolldomains.create.labels.global_informations') }}</span>
          </div>
          <div>
            <v-row>
              <v-col>
                <div class="rule-details-global-info-property">
                  <span>{{ $t('map.tolldomains.global_information.name') }}</span>
                  <v-text-field
                    id="rule-details-global-information-name"
                    v-model="tolldomainInfos.name"
                    outlined
                    dark
                    dense
                    class="rule-details-field rule-details-global-info-field"
                    hide-details
                    disabled
                  />
                </div>
              </v-col>
              <v-col>
                <div class="rule-details-global-info-property">
                  <span>{{ $t('map.tolldomains.global_information.identifier') }}</span>
                  <v-text-field
                    id="rule-details-global-information-identifier"
                    v-model.number="newRule.tollDomainId"
                    outlined
                    dark
                    dense
                    class="rule-details-field rule-details-global-info-field rule-details-global-info-code"
                    hide-details
                    :rules="identifierRules"
                    disabled
                  />
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <div class="rule-details-global-info-property">
                  <span>{{ $t('map.tolldomains.global_information.tsp_code') }}</span>
                  <v-text-field
                    id="rule-details-global-information-tsp-code"
                    v-model="tolldomainInfos.tspCode"
                    outlined
                    dark
                    dense
                    class="rule-details-field rule-details-global-info-field rule-details-global-info-tsp"
                    hide-details
                    :rules="tspRules"
                    :disabled="isReadOnly"
                  />
                </div>
              </v-col>
              <v-col class="col-auto">
                <div class="rule-details-global-info-property">
                  <span class="country-code">{{ $t('map.tolldomains.global_information.country_code') }}</span>
                  <v-text-field
                    id="rule-details-global-information-country-code"
                    v-model="tolldomainInfos.countryCode"
                    outlined
                    dark
                    dense
                    class="rule-details-field rule-details-global-info-field rule-details-global-info-code"
                    hide-details
                    disabled
                  />
                </div>
              </v-col>
              <v-col>
                <div class="rule-details-global-info-property">
                  <span>{{ $t('map.tolldomains.global_information.provider_identifier') }}</span>
                  <v-text-field
                    id="rule-details-global-information-provider-id"
                    v-model.number="tolldomainInfos.providerID"
                    outlined
                    dark
                    dense
                    class="rule-details-field rule-details-global-info-field rule-details-global-info-tsp"
                    hide-details
                    disabled
                  />
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <div>{{ $t('map.tolldomains.legends.validFrom') }}</div>
                <v-text-field
                  v-if="isReadOnly"
                  id="rule-details-global-information-validFrom"
                  outlined
                  dark
                  dense
                  disabled
                  class="rule-details-field rule-details-global-info-date-field disabled mt-2"
                  :placeholder="formatDateTime(newRule.validFrom / 1000)"
                  hide-details
                />
                <DatePicker
                  v-else
                  id="rule-details-global-information-validFrom"
                  v-model="newRule.validFrom"
                  :disabledDate="disabledDate"
                  :disabledTime="disabledTime"
                  class="rule-details-field rule-details-global-info-date-field mt-2"
                  placeholder="DD/MM/YYYY"
                  format="DD/MM/YYYY HH:mm"
                  :showTime="{ format: 'HH:mm' }"
                  @change="handleDateChange"
                />
              </v-col>
              <v-col>
                <span>{{ $t('map.tolldomains.legends.to') }}</span>
                <v-text-field
                  id="rule-details-global-information-valid-to"
                  outlined
                  dark
                  dense
                  disabled
                  class="rule-details-field rule-details-global-info-date-field disabled mt-2"
                  :placeholder="$t('map.tolldomains.global_information.until_next_start')"
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col class="pt-0">
                <span>{{ $t('map.tolldomains.global_information.description') }}</span>
                <v-textarea
                  id="rule-details-global-information-description"
                  v-model.trim="newRule.description"
                  class="mt-2"
                  rows="3"
                  dense
                  dark
                  outlined
                  hide-details
                  :rules="descriptionRules"
                  :disabled="isReadOnly"
                  :placeholder="$t('map.tolldomains.global_information.enter_description')"
                />
              </v-col>
            </v-row>
          </div>
          <span>{{ $t('map.tolldomains.global_information.service_contact_optional') }}</span>
          <v-text-field
            id="rule-details-global-information-contact"
            v-model="tolldomainInfos.contact"
            outlined
            dark
            dense
            :placeholder="$t('map.tolldomains.global_information.phone_or_email')"
            class="rule-details-field rule-details-global-info-field mt-2"
            hide-details
            :disabled="isReadOnly"
          />
        </div>
      </v-col>
      <v-col>
        <div class="rule-details-card" :class="!loadingGeoJson && !noGeoJsonData ? 'pa-0' : ''">
          <v-row no-gutters>
            <v-col id="rule-details-map-container" class="pa-0">
              <DomainsGeometryMap v-if="!loadingGeoJson && !noGeoJsonData" :GeoJson="geoJson.file" :update="showVersionHistory" />
              <v-progress-circular
                v-if="loadingGeoJson"
                indeterminate
                color="amber"
              />
              <span v-if="!loadingGeoJson && noGeoJsonData">{{ $t('map.tolldomains.no_geojson') }}</span>
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col class="version-history-container mr-2 col-auto" :class="!showVersionHistory ? 'collapsed' : 'ml-4'">
        <v-col class="rule-details-card">
          <div class="version-history-close-btn">
            <v-btn
              id="rule-details-version-history-close-btn"
              text
              fab
              x-small
              dark
              @click="closeVersionHistory()"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <div class="rule-details-card-title">
            {{ $t('map.tolldomains.version_history.title') }}
          </div>
          <div>
            <div v-if="getVersionHistory.future.length > 0" id="rule-details-version-history-future-rules" class="domains-rule-chip-container">
              <v-chip
                id="rule-details-version-history-future-chip"
                :color="themeColors.green"
                dark
                small
                class="domains-rule-chip"
              >
                {{ $t('common.future') }}
              </v-chip>
              <div v-for="rule in getVersionHistory.future" :key="rule.id" :class="{ selected: rule.id === selectedRuleId }">
                <div
                  :id="`rule-details-version-history-future-rule-${rule.id}`"
                  class="version-history-date"
                  @click="initRuleDetails(rule.id)"
                >
                  {{ formatDateTime(rule.validFrom) }} <span v-if="rule.validTo"> - {{ formatDateTime(rule.validTo) }}</span>
                </div>
              </div>
            </div>
            <div
              v-if="Object.keys(getVersionHistory.current).length > 0"
              id="rule-details-version-history-current-rule"
              class="domains-rule-chip-container"
            >
              <v-chip
                id="rule-details-version-history-current-chip"
                class="domains-rule-chip"
                :color="themeColors.secondary"
                dark
                small
              >
                {{ $t('common.current') }}
              </v-chip>
              <div
                :id="`rule-details-version-history-current-rule-${getVersionHistory.current.id}`"
                class="version-history-date"
                :class="{ selected: getVersionHistory.current.id === selectedRuleId }"
                @click="initRuleDetails(getVersionHistory.current.id)"
              >
                {{ formatDateTime(getVersionHistory.current.validFrom) }} <span v-if="getVersionHistory.current.validTo"> - {{ formatDate(getVersionHistory.current.validTo) }}</span>
              </div>
            </div>
            <div v-if="getVersionHistory.past.length > 0" id="rule-details-version-history-past-rules" class="domains-rule-chip-container mb-0">
              <v-chip
                id="rule-details-version-history-past-chip"
                class="domains-rule-chip"
                :color="themeColors.purple"
                dark
                small
              >
                {{ $t('common.past') }} - {{ $tc('common.version', getVersionHistory.past.length, { count: getVersionHistory.past.length }) }}
              </v-chip>
              <div v-for="rule in getVersionHistory.past" :key="rule.id">
                <div
                  :id="`rule-details-version-history-past-rule-${rule.id}`"
                  class="version-history-date"
                  :class="{ selected: rule.id === selectedRuleId }"
                  @click="initRuleDetails(rule.id)"
                >
                  {{ formatDateTime(rule.validFrom) }} - {{ formatDateTime(rule.validTo) }}
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="mx-2 pt-4">
        <v-tabs
          v-model="selectedNamedRuleTab"
          background-color="#191a1c"
          active-class="tab-active"
          center-active
          hide-slider
          dark
        >
          <v-tab v-for="(namedRule, itemID) in newRule.namedRules" :key="itemID">
            <span v-if="itemID == 0" id="rule-details-tab-default">{{ $t('common.default') }}</span>
            <span v-else :id="`rule-details-tab-${namedRule.name}`">{{ namedRule.name }}</span>
          </v-tab>
          <v-tab v-if="!isReadOnly" :key="newRule.namedRules ? newRule.namedRules.length : 1" @click.stop="addTab">+</v-tab>
        </v-tabs>
        <v-tabs-items v-model="selectedNamedRuleTab" dark>
          <v-tab-item v-for="(namedRule, textID) in newRule.namedRules" :key="textID">
            <v-card class="tab-content" flat>
              <v-row>
                <v-col class="col-2">
                  <span>ID</span>
                  <span v-if="$i18n.locale === 'fr'">&nbsp;:&nbsp;</span>
                  <span v-else>:&nbsp;</span>
                  <span :id="`rule-details-namedRule-nameId-${textID}`">{{ namedRule.nameId }}</span>
                </v-col>
                <v-col class="col-3 rule-details-field-flex">
                  <span>{{ $t('common.name') }}</span>
                  <span v-if="$i18n.locale === 'fr'">&nbsp;:&nbsp;</span>
                  <span v-else>:&nbsp;</span>
                  <v-text-field
                    v-if="textID == 0"
                    id="rule-details-namedRule-name-default`"
                    :value="$t('common.default')"
                    outlined
                    dark
                    dense
                    class="rule-details-field"
                    hide-details
                    :disabled="isReadOnly || textID == 0"
                  />
                  <v-text-field
                    v-else
                    :id="`rule-details-namedRule-name-${textID}`"
                    v-model="namedRule.name"
                    outlined
                    dark
                    dense
                    class="rule-details-field"
                    hide-details
                    :rules="namedRules"
                    :disabled="isReadOnly || textID == 0"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-row>
                    <v-col class="pt-0">
                      <div class="rule-details-card rule-details-content">
                        <v-row class="rule-details-status-header">
                          <span class="rule-details-card-title">{{ $t('map.tolldomains.create.labels.status') }}</span>
                        </v-row>
                        <v-row class="rule-details-status-row">
                          <v-col class="col-3 pa-0" />
                          <v-col v-for="stat in status" :key="`rule_stat_${stat.label}`" class="pt-5 pa-0">{{ $t(getStatLabel(stat.label)) }}</v-col>
                        </v-row>
                        <v-row v-for="vtrRow in vtrRows" :key="`row_${vtrRow.label}`" class="rule-details-property-row elevation-2 mt-4">
                          <v-col class="col-3">{{ $t(vtrRow.translateKey) }}</v-col>
                          <v-col v-for="(stat, index) in status" :key="`vtr_stat_${stat.label}`" class="rule-details-status-vtr pa-0">
                            <v-text-field
                              :id="`rule-details-status-${vtrRow.label}-${index}`"
                              v-model.number="namedRule[`${stat.prefix}${vtrRow.label}`]"
                              outlined
                              dark
                              dense
                              class="rule-details-field rule-details-status-field"
                              hide-details
                              :rules="statusRules"
                              :min="periodConfig.min"
                              :max="periodConfig.max"
                              :disabled="isReadOnly"
                            />
                            <span class="mx-3">{{ $t('map.tolldomains.status.labels.seconds') }}</span>
                          </v-col>
                        </v-row>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col class="pb-0">
                      <div class="rule-details-card rule-details-content-advanced">
                        <v-row class="rule-details-status-header">
                          <span class="rule-details-card-title">{{ $t('map.tolldomains.create.labels.advanced_settings') }}</span>
                        </v-row>
                        <v-row class="rule-details-property-row elevation-2 mt-4">
                          <v-col class="col-3">{{ $t('map.tolldomains.status.labels.ccc') }}</v-col>
                          <v-col class="col-3">
                            <div class="red-led ccc-status">
                              <v-select
                                id="rule-details-ccc-select"
                                v-model="namedRule.cccStatus"
                                :items="cccStatus"
                                :readonly="isReadOnly"
                                @change="namedRule.cccStatus == CccStatusEnum.Go ? namedRule.redLed = RedLedEnum.Off : namedRule.redLed"
                              />
                            </div>
                          </v-col>
                          <v-col class="col-3">
                            <div class="red-led">
                              <img :src="require(`@/assets/icons/leds/${namedRule.redLed}.svg`)" class="led-icon" alt="led-icon">
                              <v-select
                                v-if="namedRule.cccStatus == CccStatusEnum.Go"
                                id="rule-details-led-status-select"
                                v-model="namedRule.redLed"
                                :items="ledStatus"
                                append-icon=""
                                readonly
                                class="red-led-ccc-go"
                              />
                              <v-select
                                v-else
                                id="rule-details-led-status-select"
                                v-model="namedRule.redLed"
                                :readonly="isReadOnly"
                                :items="ledStatus"
                              />
                            </div>
                          </v-col>
                        </v-row>
                        <v-row v-if="isExemptionZonePossible(tolldomainInfos.tspCode)" class="rule-details-property-row elevation-2 mt-4">
                          <v-col class="col-3">{{ $t('map.tolldomains.status.labels.toll_exemption_allowed') }}</v-col>
                          <v-col class="col-3">
                            <v-switch
                              id="rule-details-toll_exemption"
                              v-model="namedRule.exemptionAllowed"
                              class="activation-switch mt-0 pt-0"
                              color="primary"
                              flat
                              inset
                              hide-details="true"
                              :label="namedRule.exemptionAllowed ? $t('common.active') : ''"
                              :disabled="isReadOnly"
                            />
                          </v-col>
                        </v-row>
                        <v-row class="rule-details-property-row elevation-2 mt-4">
                          <v-col class="col-3">{{ $t('map.tolldomains.status.labels.start_recording') }}</v-col>
                          <v-col class="col-3">
                            <v-btn-toggle
                              v-model="namedRule.vstStartRecording"
                              class="mt-0 pt-0"
                              :disabled="isReadOnly"
                              mandatory
                            >
                              <v-btn
                                id="rule-details-ignition-yes"
                                :value="'ignition'"
                                small
                              >
                                {{ $t('map.tolldomains.status.labels.at_ignition') }}
                              </v-btn>
                              <v-btn
                                id="rule-details-ignition-no"
                                :value="'movement'"
                                small
                              >
                                {{ $t('map.tolldomains.status.labels.at_movement') }}
                              </v-btn>
                            </v-btn-toggle>
                          </v-col>
                        </v-row>
                        <v-row class="rule-details-property-row elevation-2 mt-4">
                          <v-col class="col-3">{{ $t('map.tolldomains.status.labels.vtr_standstill_recording') }}</v-col>
                          <v-col class="col-3">
                            <v-switch
                              id="rule-details-vtr-standstill-switch"
                              v-model="namedRule.vtrStandstillEnabled"
                              class="activation-switch mt-0 pt-0"
                              color="primary"
                              flat
                              inset
                              hide-details="true"
                              :label="namedRule.vtrStandstillEnabled ? $t('common.active') : ''"
                              :disabled="isReadOnly"
                              @change="namedRule.vtrRecordingStandstillTimeout = namedRule.vtrStandstillEnabled ? 120 : 0"
                            />
                          </v-col>
                          <v-col class="rule-details-status-vtr pa-0">
                            <v-text-field
                              id="rule-details-vtr-standstill-text"
                              v-model.number="namedRule.vtrRecordingStandstillTimeout"
                              :title="$t('map.tolldomains.global_information.standstill_title')"
                              outlined
                              dark
                              dense
                              class="rule-details-field rule-details-status-field"
                              hide-details
                              :disabled="isReadOnly || !namedRule.vtrStandstillEnabled"
                              :rules="vtrStandstillRules"
                            />
                            <span class="mx-3">{{ $t('map.tolldomains.status.labels.seconds') }}</span>
                          </v-col>
                        </v-row>
                        <v-row class="rule-details-property-row elevation-2 mt-4">
                          <v-col class="col-3">{{ $t('map.tolldomains.status.labels.gnss_timeout_warning') }}</v-col>
                          <v-col class="rule-details-status-vtr pa-0">
                            <v-text-field
                              id="rule-details-gnss-timeout-warning"
                              v-model.number="namedRule.gnssTimeoutWarning"
                              outlined
                              dark
                              dense
                              min="1"
                              class="rule-details-field rule-details-status-field"
                              hide-details
                              :rules="gnssRules"
                              :disabled="isReadOnly"
                            />
                            <span class="mx-3">{{ $t('map.tolldomains.status.labels.seconds') }}</span>
                          </v-col>
                        </v-row>
                        <v-row class="rule-details-property-row elevation-2 mt-4">
                          <v-col class="col-3">{{ $t('map.tolldomains.status.labels.gnss_timeout_error') }}</v-col>
                          <v-col class="rule-details-status-vtr pa-0">
                            <v-text-field
                              id="rule-details-gnss-timeout-error"
                              v-model.number="namedRule.gnssTimeoutError"
                              outlined
                              dark
                              dense
                              min="1"
                              class="rule-details-field rule-details-status-field"
                              hide-details
                              :rules="gnssRules"
                              :disabled="isReadOnly"
                            />
                            <span class="mx-3">{{ $t('map.tolldomains.status.labels.seconds') }}</span>
                          </v-col>
                        </v-row>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col>
                  <div class="rule-details-card rule-details-content">
                    <span class="rule-details-card-title">{{ $t('map.tolldomains.create.labels.dsrc_instances') }}</span>
                    <div v-if="namedRule.datInstances">
                      <v-row class="mx-0 mt-4">
                        <v-col v-for="legend in legends" :key="`rule_legend_${legend}`" class="pa-0 col-3">{{ $t(legend) }}</v-col>
                      </v-row>
                      <v-row
                        v-for="(instance, id) in namedRule.datInstances"
                        :id="`rule-details-instance-${id}`"
                        :key="`instance_${id}_${instance.eid}`"
                        class="rule-details-property-row rule-details-instance-property-row elevation-2 mx-0 mt-4"
                      >
                        <v-col :id="`rule-details-instance-${id}-name`" class="col-3">{{ instance.name }}</v-col>
                        <v-col class="pa-0 col-3">
                          <v-switch
                            :id="`rule-details-instance-${id}-switch`"
                            v-model="instance.enabled"
                            class="activation-switch mt-0 pt-0"
                            color="primary"
                            flat
                            inset
                            hide-details="true"
                            :label="instance.enabled ? $t('common.active') : ''"
                            :disabled="isReadOnly"
                          />
                        </v-col>
                        <v-col class="rule-details-field-flex">
                          <v-text-field
                            :id="`rule-details-instance-${id}-context-mark-dtco`"
                            v-model="instance.contextMarkDtco"
                            outlined
                            dark
                            dense
                            class="rule-details-field rule-details-context-mark mr-3"
                            hide-details
                            :rules="isValidContextMark"
                            :disabled="isReadOnly"
                            @input="instance.contextMarkDtco = manageContextMarkDtcoInput($event)"
                          />
                        </v-col>
                        <v-col class="rule-details-field-flex">
                          <v-text-field
                            :id="`rule-details-instance-${id}-context-mark`"
                            v-model="instance.contextMark"
                            outlined
                            dark
                            dense
                            class="rule-details-field rule-details-context-mark mr-3"
                            hide-details
                            :rules="isValidContextMark"
                            :disabled="isReadOnly"
                            @input="instance.contextMark = manageContextMarkInput($event)"
                          />
                        </v-col>
                      </v-row>
                    </div>
                    <div v-else id="rule-details-no-data" class="mt-3">{{ $t('common.no_data') }}</div>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-tab-item>
          <v-tab-item :key="newRule.namedRules.length" />
        </v-tabs-items>
      </v-col>
    </v-row>
  </v-form>
</template>

<script lang="ts">

import { Component, Mixins, Watch } from 'vue-property-decorator';
import { DatePicker } from 'ant-design-vue';
import { MasterAccountInfo } from 'ctp-user-auth-service';
import { CccStatusEnum, InstanceV2, RedLedEnum, Rule, RuleLimited, TollDomainDetails } from 'ctp-toll-domain';
import moment from 'moment';
import _, { range } from 'lodash';
import Validation from '@/mixins/Validation';
import Utils from '@/mixins/Utils';
import { store } from '@/store/modules/tollDomain/types';
import { store as datInstanceStore } from '@/store/modules/datInstances/types';
import { store as persoStore } from '@/store/modules/personalisation/types';
import { store as userStore } from '@/store/modules/user/types';
import { store as permissionsStore } from '@/store/modules/permissions/types';
import './styles/rule-details.scss';
import Notification from '@/components/common/Notification/Notification.vue';
import { colors } from '@/constants/theme/colors';
import { TOLLDOMAIN_DESCRIPTION_FORBIDDEN_REGEXP,
  TOLLDOMAIN_DESCRIPTION_MAX_LENGTH,
  TOLLDOMAIN_TSP_FORBIDDEN_REGEXP,
  TOLLDOMAIN_TSP_MAX_LENGTH } from '@/constants/components/tolldomains';
import ConfirmDialog from '@/components/common/ConfirmDialog/ConfirmDialog.vue';
import DomainsGeometryMap from '../DomainsCreate/components/DomainsGeometryMap.vue';

const exemptionAllowedZone = (config.VUE_APP_EXEMPTION_ALLOWED_ZONE || process.env.VUE_APP_EXEMPTION_ALLOWED_ZONE)?.split(',') || [];

@Component({
  components: {
    ConfirmDialog,
    DatePicker,
    DomainsGeometryMap,
    Notification
  }
})
export default class RuleDetails extends Mixins(Validation, Utils) {
  @store.Action
  private fetchTollDomainInfoByID!: (payload: { id: number }) => Promise<void>;

  @store.Action
  private fetchRuleByID!: (payload: { id: number }) => Promise<void>;

  @store.Action
  private createRule!: (payload: any) => Promise<any>;

  @store.Action
  private updateRule!: (payload: any) => Promise<any>;

  @store.Getter
  private getTollDomainInfo: TollDomainDetails;

  @store.Getter
  private getRule: Rule;

  @persoStore.Action
  private fetchDeviceCount!: (payload: any) => Promise<any>;

  @persoStore.Getter
  private getDeviceCount: number;

  @userStore.Getter
  private getMasterAccounts: MasterAccountInfo[];

  @datInstanceStore.Action
  private fetchDatInstances!: (payload: any) => Promise<any>;

  @datInstanceStore.Getter
  private getDatInstances: InstanceV2[];

  @store.Action
  private fetchGeoJson!: (payload: any) => Promise<any>;

  @permissionsStore.Getter
  private getCurrentUserIsAbleTo: (readOrWrite: string, featureName: string) => boolean;

  private CccStatusEnum = CccStatusEnum;

  private RedLedEnum = RedLedEnum;

  private themeColors = colors;

  private isReadOnly = true;

  private instances: any[] = [];

  private legends = [
    'tolling.rules.instance.legends.name',
    'tolling.rules.instance.legends.activation',
    'tolling.rules.instance.legends.context_mark_dtco',
    'tolling.rules.instance.legends.context_mark_obu'
  ];

  // StartTs and EndTs are tolldomain dates while validFrom and validTo are selected rule dates
  private tolldomainInfos = {
    name: '',
    countryCode: '',
    tspCode: '',
    providerID: 0,
    contact: '',
    variantId: 0,
    startTs: 0,
    endTs: 0,
    rules: [] as RuleLimited[]
  };

  private loadingGeoJson = false;

  private noGeoJsonData = true;

  private geoJson = '';

  private showVersionHistory = true;

  private selectedNamedRuleTab = 0;

  private isNotificationOpen = false;

  private notificationContent = '';

  private notificationStatus = '';

  private valid = false;

  private selectedTab = null;

  private saving = false;

  private action = 'create';

  private periodConfig = {
    min: 1,
    max: 3600
  };

  private gnssConfig = {
    min: 0,
    max: 65535
  };

  private status = [
    { prefix: 'active', label: 'statusActive' },
    { prefix: 'ban', label: 'statusBan' },
    { prefix: 'lock', label: 'statusLock' }
  ];

  private ledStatus = [
    { text: 'LED ON', value: RedLedEnum.On },
    { text: 'LED OFF', value: RedLedEnum.Off },
    { text: 'LED Blink', value: RedLedEnum.Blink }
  ];

  private cccStatus = [
    { text: 'Ok go', value: CccStatusEnum.Go },
    { text: 'No go', value: CccStatusEnum.NoGo },
    { text: 'No contract', value: CccStatusEnum.NoGoContractual }
  ];

  private vtrRows = [
    { label: 'VtrRecord', translateKey: 'map.tolldomains.status.labels.vtr_record_period' },
    { label: 'VtrPeriod', translateKey: 'map.tolldomains.status.labels.vtr_send_period' }
  ];

  private modalData = {
    headline: 'map.tolldomains.actions.save_confirm.title',
    text: '',
    actions: {
      continue: {
        text: 'common.confirm', caller: this.save
      },
      cancel: {
        text: 'common.cancel', caller: this.cancel
      }
    }
  };

  private required = [
    this.requiredRule('common.error.forms.inputs.validator.mandatory')
  ];

  private isValidContextMark = [
    (v: string) => v === undefined || v === null || v.length === 0 || (v && this.isHexadecimal(v)) || this.$i18n.t('common.error.forms.inputs.validator.out_of_range').toString()
  ];

  private statusRules = [
    this.requiredRule('common.error.forms.inputs.validator.mandatory'),
    this.range(this.periodConfig.min, this.periodConfig.max, 'common.error.forms.inputs.validator.out_of_range')
  ];

  private descriptionRules = [
    this.maxLengthRule(TOLLDOMAIN_DESCRIPTION_MAX_LENGTH, 'common.error.forms.inputs.validator.max_length_field'),
    this.regexpRule(TOLLDOMAIN_DESCRIPTION_FORBIDDEN_REGEXP, 'common.error.forms.inputs.validator.forbidden_chars')
  ];

  private namedRules = [
    this.requiredRule('common.error.forms.inputs.validator.mandatory'),
    this.maxLengthRule(255, 'common.error.forms.inputs.validator.max_length_field')
  ];

  private tspRules = [
    this.maxLengthRule(TOLLDOMAIN_TSP_MAX_LENGTH, 'common.error.forms.inputs.validator.max_length_field'),
    this.regexpRule(TOLLDOMAIN_TSP_FORBIDDEN_REGEXP, 'common.error.forms.inputs.validator.forbidden_chars')
  ];

  private identifierRules = [
    this.requiredRule('common.error.forms.inputs.validator.mandatory'),
    this.regexpRule('\\d+', 'common.error.forms.inputs.validator.forbidden_chars')
  ];

  private vtrStandstillRules = [
    this.requiredRule('common.error.forms.inputs.validator.mandatory'),
    this.range(0, 3600, 'common.error.forms.inputs.validator.out_of_range')
  ];

  private gnssRules = [
    this.requiredRule('common.error.forms.inputs.validator.mandatory'),
    this.regexpRule('\\d+', 'common.error.forms.inputs.validator.forbidden_chars'),
    this.range(this.gnssConfig.min, this.gnssConfig.max, 'common.error.forms.inputs.validator.out_of_range')
  ];

  private masterAccount: MasterAccountInfo | undefined;

  private newRule: any = {
    validFrom: 0,
    description: '',
    namedRules: [
      {
        name: '',
        activeVtrRecord: 5,
        activeVtrPeriod: 60,
        banVtrRecord: 5,
        banVtrPeriod: 60,
        lockVtrRecord: 5,
        lockVtrPeriod: 60,
        vtrStandstillEnabled: false,
        vstStartRecording: 'ignition',
        vtrRecordingStandstillTimeout: 0,
        cccStatus: CccStatusEnum.Go,
        redLed: RedLedEnum.Off,
        gnssTimeoutWarning: 600,
        gnssTimeoutError: 1800,
        datInstances: []
      }
    ]
  };

  private saveLabel = '';

  private versionHistoryBtnLabel = 'map.tolldomains.version_history.hide';

  private history: any = {};

  private initialRuleModel: any = {};

  private ruleInfoModelChanged = false;

  private ruleModelChanged = false;

  private selectedRuleId = '';

  private async mounted() {
    this.loadingGeoJson = true;
    await this.fetchTollDomainInfoByID({ id: Number(this.$route.query.id) });
    this.newRule.tollDomainId = Number(this.$route.query.id);
    const accounts = await this.getMasterAccounts;
    this.masterAccount = accounts.find(account => account.name === this.getTollDomainInfo.masterName);
    if (this.masterAccount) {
      await this.fetchDatInstances(this.masterAccount);
    }
    await this.initRuleDetails(this.$route.query.ruleId.toString());
    this.loadingGeoJson = false;
  }

  private async initRuleDetails(ruleId: string) {
    if (this.selectedRuleId !== ruleId) {
      this.loadingGeoJson = true;
      if (ruleId) {
        this.selectedRuleId = ruleId;
        await this.fetchRuleByID({ id: Number(ruleId) });
      } else {
        const current = this.tolldomainInfos.rules.find(rule => this.isOngoing(rule.validFrom!, rule.validTo));
        // Use case: User clicks on Add a new period
        // Fetch the current rule for this period and the watcher getRule will set the correct namedRules
        // If there are only future rules, fetch the oldest future rule
        if (current) {
          await this.fetchRuleByID({ id: Number(current.id) });
        } else {
          const oldestFutureRule = this.tolldomainInfos.rules.find(rule => rule.validFrom && rule.validFrom > 0 && !this.isPast(rule.validFrom, rule.validTo));
          if (oldestFutureRule) {
            await this.fetchRuleByID({ id: Number(oldestFutureRule.id) });
          }
        }
        // Set a validFrom in the future and that is not used
        let futureValidFrom = moment().startOf('day').add(1, 'day');
        this.tolldomainInfos.rules.forEach((rule) => {
          if (rule.validFrom && futureValidFrom.isSame(moment.unix(rule.validFrom).startOf('day'))) {
            futureValidFrom = futureValidFrom.add(1, 'day');
          }
        });
        this.newRule.validFrom = futureValidFrom;
      }
      const payload: any = {
        masterId: this.masterAccount?.id,
        countryCode: this.tolldomainInfos.countryCode
      };
      if (this.tolldomainInfos.variantId) {
        payload.variantId = this.tolldomainInfos.variantId;
      }
      if (this.tolldomainInfos.endTs > 0) {
        payload.endTs = this.tolldomainInfos.endTs;
      }
      try {
        this.geoJson = await this.fetchGeoJson(payload);
      } catch (e) {
        this.geoJson = '';
      }
      this.loadingGeoJson = false;
      this.noGeoJsonData = this.geoJson.length === 0;
      this.action = this.$route.path.split('/').pop() || this.action;
      this.isReadOnly = !!(this.getRule && this.isPast(this.getRule.validFrom, this.getRule.validTo) && this.action !== 'create') || (!this.getCurrentUserIsAbleTo('write', 'rules'))
    || ((this.tolldomainInfos.endTs > 0) && moment.unix(this.tolldomainInfos.endTs).isSameOrBefore(moment(), 'minute'));
      this.fetchDeviceCount({ masterId: this.masterAccount?.id, tolldomainId: Number(this.$route.query.id), status: ['draft', 'pending', 'current'] });
    }
    this.updateSaveLabel();
  }

  addTab() {
    const defaultInstances = this.instances.map((instance: any) => {
      const defaultInstance = _.cloneDeep(instance);
      defaultInstance.enabled = false;
      defaultInstance.contextMark = null;
      return defaultInstance;
    });
    const emptyConf = {
      name: 'New',
      activeVtrRecord: 5,
      activeVtrPeriod: 60,
      banVtrRecord: 5,
      banVtrPeriod: 60,
      lockVtrRecord: 5,
      lockVtrPeriod: 60,
      vtrStandstillEnabled: false,
      vstStartRecording: 'ignition',
      vtrRecordingStandstillTimeout: 0,
      cccStatus: CccStatusEnum.NoGo,
      redLed: RedLedEnum.On,
      gnssTimeoutWarning: 600,
      gnssTimeoutError: 1800,
      datInstances: defaultInstances,
      exemptionAllowed: false
    };
    this.newRule.namedRules.push(emptyConf);
    this.selectedNamedRuleTab = this.newRule.namedRules.length - 1;
  }

  private backToRules() {
    this.$router.push({
      name: 'tolldomains-rules'
    });
  }

  private disabledDate(current: moment.Moment) {
    return current.isBefore(moment().startOf('day'));
  }

  private disabledTime(current: moment.Moment) {
    if (current && current.isBefore(moment().startOf('day').add(1, 'day'))) {
      if (current.hours() > moment().hours()) {
        return {
          disabledHours: () => range(0, moment().hours())
        };
      }
      if (current.minutes() === 59) {
        return {
          disabledHours: () => range(0, moment().hours() + 1)
        };
      }
      return {
        disabledHours: () => range(0, moment().hours()),
        disabledMinutes: () => range(0, moment().minutes() + 1)
      };
    }
    return {};
  }

  private isSameDate(timestamp1: number, timestamp2: number) {
    return moment(timestamp1).isSame(moment(timestamp2), 'day');
  }

  @Watch('getRule')
  initRule() {
    this.newRule = _.cloneDeep(this.getRule);
    this.selectedNamedRuleTab = 0;
    if (this.newRule.namedRules) {
      // Default rule config has no name attribute. Put it at the beginning of the array
      this.newRule.namedRules = [...this.newRule.namedRules.filter((rule: any) => !rule.name), ...this.newRule.namedRules.filter((rule: any) => rule.name)];
      this.newRule.namedRules.forEach((namedRule: any) => {
        // The list of instances available is a combination of instances used at one time in the rule (namedRule.datInstances[])
        // and new instances never activated on this rule (instances[] = all the rules for a given master)
        // with reduce we combine both arrays
        const instancesList = this.instances.reduce((acc, curr) => {
          const instanceFound = namedRule.datInstances.find((instance: any) => instance.eid === curr.eid);
          if (instanceFound) {
            curr = instanceFound;
          }
          acc.push({ ...curr }); // Here we spread to break the reference CTP-11082
          return acc;
        }, [] as any[]);
        this.$set(namedRule, 'datInstances', instancesList);
        this.$set(namedRule, 'vtrStandstillEnabled', namedRule.vtrRecordingStandstillTimeout > 0);
        if (!namedRule.vtrRecordingStandstillTimeout) {
          namedRule.vtrRecordingStandstillTimeout = 0;
        }
      });
      this.selectedNamedRuleTab = this.newRule.namedRules.length - 1;
    }
    this.newRule.validFrom = moment.unix(this.newRule.validFrom);
    this.initialRuleModel = _.cloneDeep(this.newRule);
  }

  @Watch('getDatInstances')
  initInstances() {
    this.instances = [];
    this.getDatInstances.forEach((instance) => {
      if (!instance.deleted) {
        this.instances.push({
          contextMark: null,
          eid: instance.eid,
          enabled: false,
          name: instance.name
        });
      }
    });
    if (this.newRule.namedRules) {
      this.newRule.namedRules[0].datInstances = this.instances;
    }
  }

  @Watch('getTollDomainInfo')
  async initTollDomainInfo() {
    if (this.getTollDomainInfo.name) {
      this.tolldomainInfos.name = this.getTollDomainInfo.name;
    }
    if (this.getTollDomainInfo.countryCode) {
      this.tolldomainInfos.countryCode = this.getTollDomainInfo.countryCode;
    }
    if (this.getTollDomainInfo.tspCode) {
      this.tolldomainInfos.tspCode = this.getTollDomainInfo.tspCode;
    }
    if (this.getTollDomainInfo.providerID) {
      this.tolldomainInfos.providerID = this.getTollDomainInfo.providerID;
    }
    if (this.getTollDomainInfo.contact) {
      this.tolldomainInfos.contact = this.getTollDomainInfo.contact;
    }
    if (this.getTollDomainInfo.variantId) {
      this.tolldomainInfos.variantId = this.getTollDomainInfo.variantId;
    }
    if (this.getTollDomainInfo.startTs) {
      this.tolldomainInfos.startTs = this.getTollDomainInfo.startTs;
    }
    if (this.getTollDomainInfo.endTs) {
      this.tolldomainInfos.endTs = this.getTollDomainInfo.endTs;
    }
    if (this.getTollDomainInfo.rules) {
      this.tolldomainInfos.rules = this.getTollDomainInfo.rules;
    }
  }

  @Watch('newRule', { deep: true })
  private modelChange() {
    this.ruleModelChanged = !_.isEqual(this.newRule, this.initialRuleModel);
  }

  @Watch('tolldomainInfos', { deep: true })
  private modelChangeInfos() {
    this.ruleInfoModelChanged = (this.tolldomainInfos.contact !== this.getTollDomainInfo.contact)
      || this.tolldomainInfos.tspCode !== this.getTollDomainInfo.tspCode;
  }

  private getStatLabel(stat: string) {
    const statLabels: any = {
      statusActive: 'map.tolldomains.status.labels.active',
      statusBan: 'map.tolldomains.status.labels.ban',
      statusLock: 'map.tolldomains.status.labels.lock'
    };
    return statLabels[stat];
  }

  private disableSave() {
    const domainRules = this.getTollDomainInfo.rules;
    let disabled = !this.valid // Form is not valid
    || this.newRule.validFrom.isSameOrBefore(moment(), 'minute') // Past or same date rules, should be a future rule
    || !(this.ruleModelChanged || this.ruleInfoModelChanged); // Some modifications
    domainRules?.forEach((r) => { // One of the rules has the same validFrom date
      if (r.validFrom && moment.unix(r.validFrom).isSame(this.newRule.validFrom) && Number(r.id) !== Number(this.newRule.id)) {
        disabled = true;
      }
    });
    const set = new Set(); // Duplicate named rule names
    const hasDuplicates = this.newRule.namedRules?.some((r: { name: string; }) => set.size === (set.add(r.name?.toLowerCase()), set.size));
    return disabled || hasDuplicates || ((this.action !== 'update') && (this.getVersionHistory.future.length > 0));
  }

  private cancel() {
    this.saving = false;
  }

  private handleSave() {
    const deviceCount = this.getDeviceCount;
    this.modalData.text = this.$tc('map.tolldomains.actions.save_confirm.label', deviceCount, { validFrom: this.formatDateTime((this.newRule.validFrom as any) / 1000), deviceCount });
    this.saving = true;
  }

  private async save() {
    this.saving = false;
    this.newRule.tollDomainType = this.getTollDomainInfo.type;
    let res: any = null;
    let action = 'create';

    if (this.action === 'create' || this.isCurrent()) {
      this.newRule.id = undefined;
      this.newRule.namedRules.map((namedRule: any) => {
        namedRule.id = undefined;
        return namedRule;
      });
    }

    const newRule = { ...this.newRule };
    newRule.validFrom = this.newRule.validFrom.unix();

    const payload: any = {
      rule: JSON.stringify(newRule),
      id: this.$route.query.id,
      tspCode: this.tolldomainInfos.tspCode
    };

    if (this.tolldomainInfos.contact) {
      payload.contact = this.tolldomainInfos.contact;
    }

    if (this.action === 'create' || this.isCurrent()) {
      res = await this.createRule(payload);
    } else if (this.getRule.id) {
      payload.id = this.getRule.id.toString();
      res = await this.updateRule(payload);
      action = 'update';
    }

    this.isNotificationOpen = true;
    this.notificationStatus = res.notificationStatus;
    if (res.code === 200) {
      this.fetchDatInstances(this.masterAccount);
      if (this.selectedRuleId) {
        await this.fetchRuleByID({
          id: Number(this.selectedRuleId)
        });
      }
      this.fetchTollDomainInfoByID({ id: Number(this.$route.query.id) }); //  This is for refreshing tolldomain info and rules after save
      this.notificationContent = this.$t(`map.tolldomains.notification.${action}.success`).toString();
    } else if (res.message) {
      this.notificationContent = `${this.$t(`map.tolldomains.notification.${action}.failure`)} : ${res.message}`;
    } else {
      this.notificationContent = this.$t(`map.tolldomains.notification.${action}.failure`).toString();
    }
  }

  private closeNotification() {
    this.isNotificationOpen = false;
    this.notificationContent = '';
    this.notificationStatus = '';
  }

  private isCurrent() {
    return this.history.current?.id === this.selectedRuleId;
  }

  // Contextmark is a 6 bytes hex value (with padding)
  // For hex testing we remove the 0 padding up to 11 so that 0 is sent for 000000000000
  private isHexadecimal(contextMark: string): boolean {
    const value = contextMark.replace(/^0{1,11}/, '');
    const hex = parseInt(value, 16);
    return hex.toString(16) === value.toLowerCase() && contextMark.length === 12;
  }

  // Replace empty string with null for backend
  private manageContextMarkInput(contextMark: string) {
    contextMark = contextMark.trim();
    if (contextMark === '') {
      return null;
    }
    return contextMark;
  }

  // Replace empty string with null for backend
  private manageContextMarkDtcoInput(contextMark: string) {
    contextMark = contextMark.trim();
    if (contextMark === '') {
      return null;
    }
    return contextMark;
  }

  private get getVersionHistory() {
    const pastRules = [] as RuleLimited[];
    let current = {};
    const futureRules = [] as RuleLimited[];
    this.getTollDomainInfo.rules?.forEach((rule) => {
      if (rule.validFrom && this.isPast(rule.validFrom, rule.validTo)) {
        pastRules.push(rule);
      } else if (rule.validFrom && this.isOngoing(rule.validFrom, rule.validTo)) {
        current = rule;
      } else {
        futureRules.push(rule);
      }
    });
    this.history = { past: pastRules.reverse(), current, future: futureRules.reverse() };
    return this.history;
  }

  private toggleVersionHistory() {
    this.showVersionHistory = !this.showVersionHistory;
    this.versionHistoryBtnLabel = this.showVersionHistory ? 'map.tolldomains.version_history.hide' : 'map.tolldomains.version_history.show';
  }

  private closeVersionHistory() {
    this.showVersionHistory = false;
  }

  private updateSaveLabel() {
    this.saveLabel = this.isOngoing(this.getRule.validFrom, this.getRule.validTo) && this.action === 'update' ? this.$t('map.tolldomains.actions.save_as').toString() : this.$t('common.save').toString();
  }

  private isExemptionZonePossible(zone: string): boolean {
    return (zone.length >= 2) && exemptionAllowedZone.includes(zone.slice(0, 2));
  }

  private handleDateChange(value: moment.Moment) {
    // We set time to minimum of 1 minute in the future as it will be required by backend
    if (value.diff(moment(), 'minute') < 2) {
      this.getRule.validFrom = value.add(2, 'minute').valueOf();
    }
  }
}
</script>
